import {
  InformationPanel,
  MFAActions,
  MFAChannel,
  MFAPhone,
  Selectors,
  useIsDesktop,
  useWidgetState,
  widgetDispatch,
} from '@eCommerce/frontend-legacy-shared-library-native';
import {useDispatch} from 'Provider';
import {View} from 'native-base';
import React, {useCallback, useEffect, useState} from 'react';

import GatedSecurityModuleInfo from 'components/GatedProfilePanels/GatedSecurityInfoModule/GatedSecurityInfoModule';
import SecurityPanel from 'components/SecurityPanel';
import MFAGate from 'containers/MFAGate';
import MFAWebViewModal from 'containers/MFAWebViewModal';
import {useMFAStepUp} from 'hooks/useMFAStepUp';
import {securityTabForwardUrl} from 'models/MFA';
import {APICallState} from 'services/api/api';
import {ProfileActions} from 'store/profile';
import {useTheme} from 'styled-components/native';

const ConnectedSecurityPanel = () => {
  const mfaStepUpHook = useMFAStepUp({forwardUrl: securityTabForwardUrl});
  const {
    onEdit,
    stopEditing,
    isPanelUnlocked,
    isMFATokenValidationSuccess,
    isEditing,
    isMFAStepUpLoading,
  } = mfaStepUpHook;
  const isDesktop = useIsDesktop();
  const dispatch = useDispatch();
  const securityEmail = useWidgetState(Selectors.securityEmailSelector) as
    | string
    | undefined;
  const securityMobilePhone = useWidgetState(
    Selectors.mfaSecurityPhoneSelector
  ) as MFAPhone | undefined;
  const mfaChannelPreference = useWidgetState(
    Selectors.mfaChannelPreferenceSelector
  ) as MFAChannel | undefined;
  const isVIP = useWidgetState(Selectors.isVIPSelector) as boolean;
  const mfaProfileStatus = useWidgetState(
    Selectors.mfaProfileStatusSelector
  ) as APICallState;
  const editOptinState = useWidgetState(
    Selectors.editOptinStateSelector
  ) as APICallState;
  const editProfileState = useWidgetState(
    Selectors.editProfileStateSelector
  ) as APICallState;
  const [mfaWebViewUrl, setMFAWebViewUrl] = useState<string | undefined>();

  const onSave = useCallback((channel: MFAChannel) => {
    widgetDispatch(
      MFAActions.changeChannelPreference({channelPreference: channel})
    );
  }, []);

  const onOptout = useCallback(() => {
    widgetDispatch(MFAActions.editOptinStatus(false));
  }, []);

  const theme = useTheme();

  /*  Edge Case in the future
   We want to check the URL params to see if the Panel is unlocked
   If the user has already steped up but lingered long enough that their token has expired
   When they then go to press the edit button they will have to step up again (which is fine)
   but then after stepping up again they will be returned to the read only state.

   Future possible Behaviour:
   After the user re-steps up, check the params and if the isUnlocked param is there in the url
   then keep the panel in an edit state.
  */
  useEffect(() => {
    stopEditing();
  }, [stopEditing]);

  const onEditPress = () => {
    onEdit();
    widgetDispatch(MFAActions.resetEditSecurityProfile());
  };

  const onOverlayAnimationFinish = () => {
    if (editProfileState === APICallState.SUCCESS) {
      stopEditing();
    }
    dispatch(ProfileActions.resetProfileInfoState());
  };

  const isLoading =
    isMFAStepUpLoading ||
    [APICallState.LOADING, APICallState.INITIAL].includes(mfaProfileStatus) ||
    [APICallState.LOADING].includes(editOptinState);

  return (
    <MFAGate
      forwardUrl={securityTabForwardUrl}
      GatedComponent={GatedSecurityModuleInfo}>
      {mfaProfileStatus === APICallState.ERROR ? (
        <InformationPanel
          isSVG={false}
          iconName="exclamation-circle"
          iconColor={theme.colors.error}
          titleText="Well, this is embarrassing."
          iconSize={{height: 72, width: 72}}
          iconIsLight={true}
          upperDescriptionText="There was an error retrieving your security information. Please try again later."
        />
      ) : (
        <>
          <View testID={isLoading ? 'security-panel-loading' : undefined} />
          <SecurityPanel
            isLoading={isLoading}
            email={securityEmail}
            phoneNumber={securityMobilePhone}
            channelPreference={mfaChannelPreference}
            isVIP={isVIP}
            isUnlocked={isPanelUnlocked}
            onEdit={onEditPress}
            onSave={onSave}
            onCancel={stopEditing}
            savingState={editProfileState}
            isMFATokenValidationSuccess={isMFATokenValidationSuccess}
            editOptinState={editOptinState}
            onOptout={onOptout}
            onOverlayAnimationFinish={onOverlayAnimationFinish}
            isEditLoading={isEditing && isMFAStepUpLoading}
            setMFAWebViewUrl={setMFAWebViewUrl}
          />
        </>
      )}
      <MFAWebViewModal
        mfaOptinStatus={undefined}
        onClose={() => {
          setMFAWebViewUrl(undefined);
          widgetDispatch(MFAActions.getSecurityProfile());
          stopEditing();
        }}
        isModalVisible={!!mfaWebViewUrl}
        uri={mfaWebViewUrl}
      />
    </MFAGate>
  );
};

export default ConnectedSecurityPanel;
